import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/components/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Students from "@/pages/dashboard/Students";
import StudentProfile from "@/pages/dashboard/StudentProfile";
import Schedule from "@/pages/dashboard/Schedule";
import AIPicks from "@/pages/dashboard/Resources";
import ResourcesHub from "@/pages/dashboard/ResourcesHub";
import Billing from "@/pages/dashboard/Billing";
import Growth from "@/pages/dashboard/Growth";
import AdminBlog from "@/pages/dashboard/AdminBlog";
import Worksheets from "@/pages/dashboard/Worksheets";
import Receipts from "@/pages/dashboard/Receipts";
import Referrals from "@/pages/dashboard/Referrals";
import BillingSuccess from "@/pages/BillingSuccess";
import PublicPortfolio from "@/pages/PublicPortfolio";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import SuggestResource from "@/pages/SuggestResource";
import AdminFeedback from "@/pages/dashboard/AdminFeedback";
import AdminSuggestions from "@/pages/dashboard/AdminSuggestions";
import AdminRevenue from "@/pages/dashboard/AdminRevenue";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-stone-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
              <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
              <Route path="/reset-password" element={<GuestOnly><ResetPassword /></GuestOnly>} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/suggest-a-resource" element={<SuggestResource />} />
              <Route path="/portfolio/share/:token" element={<PublicPortfolio />} />
              <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
              <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
                <Route index element={<Overview />} />
                <Route path="students" element={<Students />} />
                <Route path="students/:id" element={<StudentProfile />} />
                <Route path="schedule" element={<Schedule />} />
                <Route path="ai-picks" element={<AIPicks />} />
                <Route path="resources" element={<ResourcesHub />} />
                <Route path="billing" element={<Billing />} />
                <Route path="worksheets" element={<Worksheets />} />
                <Route path="receipts" element={<Receipts />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="growth" element={<Growth />} />
                <Route path="admin/blog" element={<AdminBlog />} />
                <Route path="admin/feedback" element={<AdminFeedback />} />
                <Route path="admin/suggestions" element={<AdminSuggestions />} />
                <Route path="admin/revenue" element={<AdminRevenue />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
